"use client";

import { AnimatePresence, m as motion, useReducedMotion } from "framer-motion";
import { useId, useMemo, useState } from "react";

import { RevealOnScroll } from "@/components/primitives/RevealOnScroll";
import { faqSchema, jsonLdString } from "@/lib/structured-data";

export type FaqItem = {
  q: string;
  a: string;
};

type Props = {
  label: string;
  items: ReadonlyArray<FaqItem>;
};

export function FaqSection({ label, items }: Props) {
  const jsonLd = useMemo(() => jsonLdString(faqSchema(items)), [items]);

  if (!items || items.length === 0) return null;

  return (
    <section className="mx-auto w-full max-w-[1360px] px-5 py-16 md:px-10 md:py-24">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLd }}
      />
      <RevealOnScroll>
        <span
          className="mb-10 inline-flex items-center gap-[10px] font-mono text-[11px] uppercase text-fg3 md:mb-14"
          style={{ letterSpacing: "0.2em" }}
        >
          <span
            aria-hidden
            className="inline-block h-2 w-2 rounded-full"
            style={{
              background: "var(--accent)",
              boxShadow: "0 0 10px var(--accent)",
            }}
          />
          {label}
        </span>
      </RevealOnScroll>

      <ul
        className="divide-y border-y"
        style={{ borderColor: "var(--border)" }}
      >
        {items.map((item, i) => (
          <FaqRow key={i} item={item} index={i} />
        ))}
      </ul>
    </section>
  );
}

type RowProps = {
  item: FaqItem;
  index: number;
};

function FaqRow({ item, index }: RowProps) {
  const [open, setOpen] = useState(false);
  const prefersReduced = useReducedMotion();
  const id = useId();
  const buttonId = `${id}-btn`;
  const panelId = `${id}-panel`;

  return (
    <RevealOnScroll delay={index * 0.04}>
      <li
        className="list-none"
        style={{ borderColor: "var(--border)" }}
      >
        <button
          id={buttonId}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className="group flex w-full items-start justify-between gap-6 py-6 text-left md:py-8"
          style={{ background: "transparent" }}
        >
          <span className="flex flex-1 items-start gap-5">
            <span
              className="mt-[2px] font-mono text-[11px] uppercase text-brand-azure md:mt-[4px]"
              style={{ letterSpacing: "0.2em" }}
            >
              {String(index + 1).padStart(2, "0")}
            </span>
            <span
              className="font-display font-semibold text-fg1 transition-colors group-hover:text-brand-azure md:text-[22px]"
              style={{
                fontSize: "clamp(17px, 1.9vw, 22px)",
                lineHeight: 1.3,
                letterSpacing: "-0.01em",
                transitionDuration: "220ms",
                transitionTimingFunction: "var(--ease-out)",
              }}
            >
              {item.q}
            </span>
          </span>
          <span
            aria-hidden
            className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border font-mono text-[14px] text-fg2 transition-colors group-hover:border-brand-azure group-hover:text-brand-azure"
            style={{
              borderColor: "var(--border-strong)",
              transitionDuration: "220ms",
              transitionTimingFunction: "var(--ease-out)",
            }}
          >
            {open ? "−" : "+"}
          </span>
        </button>

        <AnimatePresence initial={false}>
          {open ? (
            <motion.div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              initial={prefersReduced ? { opacity: 1 } : { height: 0, opacity: 0 }}
              animate={prefersReduced ? { opacity: 1 } : { height: "auto", opacity: 1 }}
              exit={prefersReduced ? { opacity: 0 } : { height: 0, opacity: 0 }}
              transition={{
                duration: prefersReduced ? 0 : 0.42,
                ease: [0.22, 1, 0.36, 1],
              }}
              style={{ overflow: "hidden" }}
            >
              <p
                className="max-w-[780px] pb-6 pl-[52px] text-[15px] leading-relaxed text-fg2 md:pb-8 md:pl-[64px] md:text-[16px]"
              >
                {item.a}
              </p>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </li>
    </RevealOnScroll>
  );
}
